import * as React from "react";
import { FluentProvider, webDarkTheme, webLightTheme } from "@fluentui/react-components";

/* global window, MediaQueryList, MediaQueryListEvent */

/**
 * Tracks the OS/browser colour scheme.
 *
 * Outlook also exposes Office.context.officeTheme, but that only exists once
 * an Office host has initialised, and the pane is meant to render before then
 * (and to work in a plain browser). prefers-color-scheme is what the host's
 * own webview reports anyway.
 */
function usePrefersDark(): boolean {
  const query = "(prefers-color-scheme: dark)";
  const [dark, setDark] = React.useState<boolean>(() => {
    try {
      return window.matchMedia?.(query).matches ?? false;
    } catch {
      return false;
    }
  });

  React.useEffect(() => {
    let media: MediaQueryList;
    try {
      media = window.matchMedia(query);
    } catch {
      return undefined;
    }
    const onChange = (event: MediaQueryListEvent) => setDark(event.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  return dark;
}

/**
 * The Fluent provider both entry points share.
 *
 * applyStylesToPortals matters here: the task pane runs inside an iframe, and
 * anything Fluent renders through a portal would otherwise land unstyled.
 */
export const ThemedRoot: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dark = usePrefersDark();
  return (
    <FluentProvider theme={dark ? webDarkTheme : webLightTheme} applyStylesToPortals>
      {children}
    </FluentProvider>
  );
};
